import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { FileSignature, Eye, Loader2, ExternalLink, Share2, Download } from 'lucide-react';
import { BrokerAgreementWithDetails } from '../types';
import { fetchRenterAgreements, downloadAgreementPDF } from '../lib/brokerAgreements';
import { shareAgreementAsPDF } from '../lib/pdfGenerator';
import { useNavigate } from 'react-router-dom';

interface MyAgreementsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Helper function to detect if text is Hebrew (starts with Hebrew characters)
function isHebrewText(text: string): boolean {
  if (!text || text.trim().length === 0) return false;
  // Check if the first non-whitespace character is Hebrew (Unicode range: \u0590-\u05FF)
  const firstChar = text.trim().charAt(0);
  return /[\u0590-\u05FF]/.test(firstChar);
}

export function MyAgreementsModal({ isOpen, onClose }: MyAgreementsModalProps) {
  const { t } = useTranslation();
  const [agreements, setAgreements] = useState<BrokerAgreementWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgreement, setSelectedAgreement] = useState<BrokerAgreementWithDetails | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      loadAgreements();
    }
  }, [isOpen]);

  const loadAgreements = async () => {
    setLoading(true);
    try {
      const data = await fetchRenterAgreements();
      setAgreements(data);
    } catch (error) {
      console.error('Error loading agreements:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSignatureUrl = async (agreement: BrokerAgreementWithDetails) => {
    setSignatureLoading(true);
    setSignatureUrl(null);
    
    try {
      // First try the public URL directly
      const publicUrl = agreement.signature_url;
      
      // Try to fetch it to see if it works
      const response = await fetch(publicUrl, { method: 'HEAD' });
      
      if (response.ok) {
        setSignatureUrl(publicUrl);
        return;
      }
      
      // If public URL fails, try to get a signed URL
      console.log('Public URL failed, trying signed URL...');
      const { supabase } = await import('../lib/supabase');
      
      // Extract the file path from the public URL
      const urlParts = publicUrl.split('/signatures/');
      if (urlParts.length > 1) {
        const filePath = urlParts[1];
        
        const { data, error } = await supabase.storage
          .from('signatures')
          .createSignedUrl(filePath, 3600); // 1 hour expiry
        
        if (data?.signedUrl && !error) {
          setSignatureUrl(data.signedUrl);
        } else {
          console.error('Failed to create signed URL:', error);
          setSignatureUrl(publicUrl); // Fallback to public URL anyway
        }
      } else {
        setSignatureUrl(publicUrl);
      }
    } catch (error) {
      console.error('Error loading signature:', error);
      setSignatureUrl(agreement.signature_url); // Fallback to original URL
    } finally {
      setSignatureLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAgreement) {
      loadSignatureUrl(selectedAgreement);
    }
  }, [selectedAgreement]);

  const handleViewApartment = (apartmentId: string) => {
    onClose();
    navigate(`/apartment/${apartmentId}`);
  };

  const handleShareAgreement = async () => {
    if (!selectedAgreement) return;

    setSharing(true);
    try {
      await shareAgreementAsPDF(selectedAgreement, signatureUrl);
    } catch (error) {
      console.error('Error sharing agreement:', error);
    } finally {
      setSharing(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedAgreement) return;

    setDownloading(true);
    try {
      const title = selectedAgreement.apartment_title || 'Agreement';
      await downloadAgreementPDF(selectedAgreement.id, title);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen && !selectedAgreement} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto overflow-x-hidden py-0 px-[10px]">
          <div className="p-6">
            <DialogHeader className="pb-4">
              <DialogTitle className="flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-blue-600" />
                {t('agreements.mySignedAgreements')}
              </DialogTitle>
              <DialogDescription>
                {t('agreements.viewAllAgreements')}
              </DialogDescription>
            </DialogHeader>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">{t('agreements.loadingAgreements')}</p>
                </div>
              </div>
            ) : agreements.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                <FileSignature className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-700 mb-1">{t('agreements.noAgreements')}</h3>
                <p className="text-sm text-gray-600">
                  {t('agreements.noAgreementsDescription')}
                </p>
              </div>
            ) : (
              <div className="space-y-3 pb-1">
              {agreements.map((agreement) => (
                <div
                  key={agreement.id}
                  className="bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-300 transition-all"
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-end">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedAgreement(agreement)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {t('agreements.view')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewApartment(agreement.apartment_id)}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">
                        {agreement.apartment_title || 'Apartment'}
                      </h4>
                      {agreement.apartment_address && (
                        <p className="text-sm text-gray-600 mb-2">
                          {agreement.apartment_address}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <FileSignature className="w-3 h-3" />
                        <span>
                          Signed {new Date(agreement.signed_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Agreement Detail Modal */}
      <Dialog open={!!selectedAgreement} onOpenChange={() => setSelectedAgreement(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>{t('agreements.agreementDetails')}</DialogTitle>
            <DialogDescription>
              {t('agreements.reviewAgreement')}
            </DialogDescription>
          </DialogHeader>
          {selectedAgreement && (
            <div className="space-y-4 overflow-x-hidden">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-sm text-gray-700 mb-2">{t('agreements.agreementInfo')}</h4>
                <div className="space-y-1 text-sm break-words">
                  {selectedAgreement.renter_name && (
                    <p className="break-words"><strong>Renter Name:</strong> {selectedAgreement.renter_name}</p>
                  )}
                  {selectedAgreement.renter_id_number && (
                    <p className="break-words"><strong>ID Number:</strong> {selectedAgreement.renter_id_number}</p>
                  )}
                  <p className="break-words"><strong>Property:</strong> {selectedAgreement.apartment_title || 'Apartment'}</p>
                  {selectedAgreement.apartment_address && (
                    <p className="break-words"><strong>Address:</strong> {selectedAgreement.apartment_address}</p>
                  )}
                  <p><strong>Signed:</strong> {selectedAgreement.signed_at ? new Date(selectedAgreement.signed_at).toLocaleString() : 'N/A'}</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-sm text-gray-700 mb-2">{t('agreements.yourSignature')}</h4>
                <div className="flex justify-center items-center bg-gray-50 p-4 rounded border border-gray-200 min-h-[160px]">
                  {signatureLoading ? (
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-gray-600">{t('agreements.loadingSignature')}</p>
                    </div>
                  ) : signatureUrl ? (
                    <img
                      src={signatureUrl}
                      alt="Your Signature"
                      className="max-w-full h-auto max-h-40 object-contain"
                      onError={(e) => {
                        console.error('Failed to load signature image:', signatureUrl);
                        (e.target as HTMLImageElement).style.display = 'none';
                        const parent = (e.target as HTMLImageElement).parentElement;
                        if (parent) {
                          parent.innerHTML = '<div class="text-center"><p class="text-sm text-red-600 mb-2">Unable to load signature</p><p class="text-xs text-gray-500">The image may have been moved or deleted</p></div>';
                        }
                      }}
                    />
                  ) : (
                    <div className="text-center">
                      <p className="text-sm text-gray-600">{t('agreements.noSignature')}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 overflow-hidden">
                <h4 className="font-semibold text-sm text-gray-700 mb-2">{t('agreements.agreementText')}</h4>
                {(() => {
                  const agreementText = selectedAgreement.agreement_text || '';
                  const isRTL = isHebrewText(agreementText);
                  return (
                    <pre 
                      dir={isRTL ? 'rtl' : 'ltr'}
                      className={`whitespace-pre-wrap break-words text-xs text-gray-700 font-sans leading-relaxed max-h-60 overflow-y-auto overflow-x-hidden max-w-full ${isRTL ? 'rtl-text-block' : ''}`}
                      style={{ 
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        fontFamily: isRTL ? 'Arial, "Helvetica Neue", Helvetica, sans-serif' : 'Georgia, "Times New Roman", Times, serif',
                        textAlign: isRTL ? 'right' : 'left',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }}
                    >
                      {agreementText}
                    </pre>
                  );
                })()}
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Button
                    onClick={handleDownloadPDF}
                    disabled={downloading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {downloading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('common.downloading', 'Generating...')}
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        {t('common.downloadPDF', 'Download PDF')}
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleShareAgreement}
                    disabled={sharing}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {sharing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('common.sharing', 'Sharing...')}
                      </>
                    ) : (
                      <>
                        <Share2 className="w-4 h-4 mr-2" />
                        {t('common.share', 'Share')}
                      </>
                    )}
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedAgreement(null)}
                    className="flex-1"
                  >
                    {t('common.close')}
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedAgreement(null);
                      handleViewApartment(selectedAgreement.apartment_id);
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    {t('agreements.viewProperty')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

